import type { ReactNode } from 'react';
import { AlertCircle, AlertTriangle, CheckCircle2, Info } from 'lucide-react';
import { clsx } from 'clsx';

type AlertVariant = 'info' | 'success' | 'warning' | 'danger' | 'neutral';

interface AlertProps {
  children: ReactNode;
  className?: string;
  title?: ReactNode;
  variant?: AlertVariant;
}

const styles: Record<AlertVariant, string> = {
  info: 'border-blue-200 bg-blue-50 text-blue-800 dark:border-blue-900 dark:bg-blue-950/40 dark:text-blue-200',
  success: 'border-green-200 bg-green-50 text-green-800 dark:border-green-900 dark:bg-green-950/40 dark:text-green-200',
  warning: 'border-amber-200 bg-amber-50 text-amber-900 dark:border-amber-900 dark:bg-amber-950/40 dark:text-amber-200',
  danger: 'border-red-200 bg-red-50 text-red-800 dark:border-red-900 dark:bg-red-950/40 dark:text-red-200',
  neutral: 'border-border-default bg-surface-raised text-content-secondary',
};

const icons = {
  info: Info,
  success: CheckCircle2,
  warning: AlertTriangle,
  danger: AlertCircle,
  neutral: Info,
};

export function Alert({ children, className = '', title, variant = 'info' }: AlertProps) {
  const Icon = icons[variant];

  return (
    <div className={clsx('flex gap-3 rounded-lg border p-4 text-sm', styles[variant], className)}>
      <Icon className="mt-0.5 h-4 w-4 flex-shrink-0" aria-hidden="true" />
      <div className="min-w-0">
        {title ? <div className="mb-1 font-semibold">{title}</div> : null}
        <div>{children}</div>
      </div>
    </div>
  );
}
